import { useEffect } from "react";
import { IS_E2E_TEST_MODE } from "../../../lib/loggerApi";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import { useMatchLogStore } from "../../../store/useMatchLogStore";
import type {
  ActionStep,
  GameStoppageSummary,
  LoggerHarness,
  Match,
  QueuedEventSummary,
} from "../types";

interface UseCockpitHarnessParams {
  enabled: boolean;
  match: Match | null;
  resetFlow: () => void;
  setSelectedTeam: (team: "home" | "away") => void;
  currentStepRef: React.MutableRefObject<ActionStep>;
  sendPassEvent: (options: {
    team: "home" | "away";
    passerId: string;
    recipientId: string;
  }) => void;
  sendRawEvent: (payload: Record<string, any>) => void;
  undoLastEvent: () => Promise<void> | void;
  computedDriftSeconds: number;
  forcedDriftSeconds: number | null;
  driftSeconds: number;
  showDriftNudge: boolean;
  isBallInPlay: boolean;
  effectiveClock: string;
  ineffectiveClock: string;
}

export const useCockpitHarness = ({
  enabled,
  match,
  resetFlow,
  setSelectedTeam,
  currentStepRef,
  sendPassEvent,
  sendRawEvent,
  undoLastEvent,
  computedDriftSeconds,
  forcedDriftSeconds,
  driftSeconds,
  showDriftNudge,
  isBallInPlay,
  effectiveClock,
  ineffectiveClock,
}: UseCockpitHarnessParams): void => {
  useEffect(() => {
    if (!IS_E2E_TEST_MODE) return;
    if (!enabled || !match) return;

    const harness: LoggerHarness = {
      resetFlow,
      setSelectedTeam,
      getCurrentStep: () => currentStepRef.current,
      sendPassEvent,
      sendRawEvent,
      getMatchContext: () => ({
        matchId: match.id,
        homeTeamId: match.home_team.id,
        awayTeamId: match.away_team.id,
      }),
      undoLastEvent: () => undoLastEvent(),
      getQueueSnapshot: () => {
        const summarize = (events: MatchEvent[]): QueuedEventSummary[] =>
          events.map((event) => ({
            match_id: event.match_id,
            timestamp: event.timestamp,
            client_id: event.client_id,
            type: event.type,
          }));
        const state = useMatchLogStore.getState();
        return {
          currentMatchId: state.currentMatchId,
          queuedEvents: summarize(state.queuedEvents),
          queuedEventsByMatch: Object.fromEntries(
            Object.entries(state.queuedEventsByMatch).map(
              ([matchKey, events]) => [matchKey, summarize(events)],
            ),
          ),
        };
      },
      getLiveEventSummary: () => {
        const state = useMatchLogStore.getState();
        return {
          liveCount: state.liveEvents.length,
          gameStoppageCount: state.liveEvents.filter(
            (event) => event.type === "GameStoppage",
          ).length,
        };
      },
      // PMA-COCK-111: content-level equivalent of getLiveEventSummary,
      // added so E2E specs that used to assert on LiveEventFeed's DOM
      // (removed from the cockpit's render path) can verify event
      // type/content directly, without needing any feed UI mounted.
      getLiveEvents: () => {
        const state = useMatchLogStore.getState();
        return state.liveEvents.map((event) => ({
          type: event.type,
          team_id: event.team_id,
          player_id: event.player_id ?? null,
          staff_id: event.staff_id ?? null,
          notes: event.notes ?? null,
          match_clock: event.match_clock,
          period: event.period,
          has_id: Boolean(event._id),
          // EPIC-97 (AC1): top-level origin `location`, sibling to `data` —
          // was entirely absent from this harness shape, making the
          // flip-rotation origin-tap invariant unreachable from a real
          // browser (only `data.end_location` was readable). See
          // `LiveEventDetail`'s own doc comment (types.ts).
          location: event.location ?? null,
          data: event.data ?? {},
          // Task #67: exposed so E2E can poll for a specific event's
          // removal by identity (waitForEventRemoved, e2e/utils/logger.ts)
          // instead of a content match -- match_clock alone is not unique
          // (repeat-each runs and dense sequences both produced multiple
          // live events sharing a clock in practice).
          client_id: event.client_id ?? null,
        }));
      },
      getRecentGameStoppages: () => {
        const summarize = (event: MatchEvent): GameStoppageSummary => ({
          team_id: event.team_id,
          match_clock: event.match_clock,
          notes: event.notes,
          stoppage_type: String(event.data?.stoppage_type || ""),
          reason:
            typeof event.data?.reason === "string" ? event.data.reason : null,
          trigger_action:
            typeof event.data?.trigger_action === "string"
              ? event.data.trigger_action
              : null,
          trigger_team_id:
            typeof event.data?.trigger_team_id === "string"
              ? event.data.trigger_team_id
              : null,
        });
        const state = useMatchLogStore.getState();
        return state.liveEvents
          .filter((event) => event.type === "GameStoppage")
          .slice(-6)
          .map(summarize);
      },
      clearQueue: () => {
        useMatchLogStore.getState().clearQueuedEvents();
      },
      getDriftSnapshot: () => ({
        computed: computedDriftSeconds,
        forced: forcedDriftSeconds,
        effective: driftSeconds,
        show: showDriftNudge,
      }),
      // PMA-COCK-115: content-level equivalent of the removed
      // `ball-state-label` DOM node — the label was removed from
      // MatchTimerDisplay.tsx, but clock start/stop still drives
      // `isBallInPlay` exactly as before.
      getBallState: () => ({ isBallInPlay }),
      // PMA-COCK-118: content-level equivalent of the removed
      // `effective-clock-value`/`ineffective-clock-value` DOM nodes —
      // MatchTimerDisplay.tsx no longer renders the "Tiempo Efectivo"/
      // "Tiempo Inefectivo" cards, but the underlying clock strings are
      // unchanged.
      getEffectiveIneffectiveClocks: () => ({
        effectiveClock,
        ineffectiveClock,
      }),
    };

    window.__PROMATCH_LOGGER_HARNESS__ = harness;

    return () => {
      delete window.__PROMATCH_LOGGER_HARNESS__;
    };
  }, [
    enabled,
    match,
    resetFlow,
    setSelectedTeam,
    currentStepRef,
    sendPassEvent,
    sendRawEvent,
    undoLastEvent,
    computedDriftSeconds,
    forcedDriftSeconds,
    driftSeconds,
    showDriftNudge,
    isBallInPlay,
    effectiveClock,
    ineffectiveClock,
  ]);
};
